import React, { Component } from 'react';
import Breadcrumb from './breadcrumb';
import { Button, Table, Card, Nav, Tab, ProgressBar, Badge } from 'react-bootstrap';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Alert
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { Popover, Box } from '@mui/material';
import toastr from 'toastr';
import {
    uploadCSV,
    getCollectionFields,
    getCollectionSchema,
    getAvailableCollections,
    getMappingRules,
    getMappingRuleById,
    createMappingRule,
    updateMappingRule,
    executeMigration,
    getMigrationStatus,
    getMigrationJobs,
    previewMigration,
    getDefaultMappingRules,
    initializeDefaultRules,
    getLegacyEntityTypes,
    getLegacyEntitySample,
    getLegacyEntityHeaders,
    getLegacyEntityCount,
    executeLegacyMigration,
    previewLegacyMigration
} from '../../../services/clinicPortalServices/migrationService';

class ClinicMigrationTool extends Component {
    constructor(props) {
        super(props);

        this.state = {
            // Collections
            collections: [],
            selectedCollection: '',
            collectionSchema: {},

            // Legacy Data
            legacyEntityTypes: [],
            selectedEntityType: '',
            entityHeaders: [],
            entitySample: [],
            entityCount: 0,

            // Mapping Rules
            mappingRules: [],
            selectedMappingRule: null,
            showMappingDialog: false,
            editingMapping: null,

            // Current Mapping
            currentMapping: {
                name: '',
                description: '',
                targetCollection: '',
                sourceEntity: '',
                fieldMappings: [],
                collectionConfig: {
                    uniqueFields: [],
                    updateStrategy: 'skip'
                },
                options: {
                    batchSize: 1000,
                    skipFirstRow: true,
                    continueOnError: true,
                    validateBeforeInsert: true
                }
            },

            // Migration Status
            activeTab: 'upload',
            migrationJobs: [],
            currentJob: null,
            migrationInProgress: false,

            // UI State
            loading: false,
            errors: [],

            // Column Preview State
            previewAnchorEl: null,
            previewColumn: null,
            previewData: []
        };

        this.statusCheckInterval = null;
    }

    componentDidMount() {
        this.loadCollections();
        this.loadMappingRules();
        this.loadMigrationJobs();
        this.loadLegacyEntityTypes();
        this.checkForDefaultRules();
    }

    componentWillUnmount() {
        if (this.statusCheckInterval) {
            clearInterval(this.statusCheckInterval);
        }
    }

    loadCollections = async () => {
        try {
            const response = await getAvailableCollections();
            if (response && response.success) {
                this.setState({ collections: response.data });
            }
        } catch (error) {
            toastr.error('Failed to load collections');
        }
    };

    loadMappingRules = async () => {
        try {
            const response = await getMappingRules();
            if (response && response.success) {
                this.setState({ mappingRules: response.data });
            }
        } catch (error) {
            toastr.error('Failed to load mapping rules');
        }
    };

    loadMigrationJobs = async () => {
        try {
            const response = await getMigrationJobs({ limit: 10 });
            if (response && response.success) {
                this.setState({ migrationJobs: response.data });
            }
        } catch (error) {
            console.error('Failed to load migration jobs', error);
        }
    };

    checkForDefaultRules = async () => {
        try {
            const response = await getDefaultMappingRules();
            if (response && response.data && response.data.length === 0) {
                // const result = window.confirm("No default mapping rules found. Would you like to initialize them?");
                // if (result) {
                //     await initializeDefaultRules();
                //     toastr.success("Default mapping rules initialized");
                //     this.loadMappingRules();
                // }
            }
        } catch (error) {
            console.error('Failed to check default rules', error);
        }
    };

    loadLegacyEntityTypes = async () => {
        try {
            const response = await getLegacyEntityTypes();
            if (response && response.success) {
                this.setState({ legacyEntityTypes: response.data });
            }
        } catch (error) {
            toastr.error('Failed to load legacy entity types');
        }
    };

    handleEntityTypeChange = async (entityType) => {
        if (!entityType) {
            this.setState({
                selectedEntityType: '',
                entityHeaders: [],
                entitySample: [],
                entityCount: 0
            });
            return;
        }

        this.setState({ loading: true, selectedEntityType: entityType });

        try {
            // Load entity headers, sample data, and count in parallel
            const [headersResponse, sampleResponse, countResponse] = await Promise.all([
                getLegacyEntityHeaders(entityType),
                getLegacyEntitySample(entityType, 5),
                getLegacyEntityCount(entityType)
            ]);

            if (headersResponse && headersResponse.success) {
                this.setState({
                    entityHeaders: headersResponse.data,
                    entitySample: sampleResponse.success ? sampleResponse.data : [],
                    entityCount: countResponse.success ? countResponse.count : 0
                });
            }
        } catch (error) {
            toastr.error('Failed to load entity data');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleCollectionChange = async (e) => {
        const collection = e.target.value;
        this.setState({ selectedCollection: collection, loading: true });

        try {
            // Load collection schema
            const schemaResponse = await getCollectionSchema(collection);
            if (schemaResponse && schemaResponse.success) {
                const schema = schemaResponse.data.schema || {};

                // Load mapping rules for this collection
                const rulesResponse = await getMappingRules({ collection });
                const collectionRules = rulesResponse && rulesResponse.success ? rulesResponse.data : [];

                // Initialize field mappings based on schema
                const fieldMappings = [];
                const processedFields = new Set();

                Object.keys(schema).forEach((field) => {
                    const schemaField = schema[field];

                    // Skip address subfields if we already processed the address object
                    if (field.startsWith('address.') && processedFields.has('address')) {
                        return;
                    }

                    // Handle address object specially
                    if (field === 'address' && schemaField.type === 'Object') {
                        // Check if there are address subfields
                        const addressSubfields = Object.keys(schema).filter((f) => f.startsWith('address.'));
                        if (addressSubfields.length > 0) {
                            // Mark as processed
                            processedFields.add('address');
                            addressSubfields.forEach((f) => processedFields.add(f));

                            // Create a single mapping for the address object
                            fieldMappings.push({
                                targetField: 'address',
                                targetFieldType: 'AddressObject',
                                required: schemaField.required || false,
                                sourceField: '',
                                sourceType: 'addressParse',
                                sourceConfig: {},
                                transformation: 'none',
                                transformationConfig: {},
                                validation: {},
                                isAddressField: true
                            });
                            return;
                        }
                    }

                    // Regular field processing
                    if (!processedFields.has(field)) {
                        processedFields.add(field);

                        // Start with empty validation - user must explicitly enable validations
                        const validation = {};

                        fieldMappings.push({
                            targetField: field,
                            targetFieldType: schemaField.type || 'String',
                            required: schemaField.required || false,
                            sourceField: '',
                            sourceType: 'direct',
                            sourceConfig: {},
                            transformation: 'none',
                            transformationConfig: {},
                            validation: validation // Empty by default - user must enable
                        });
                    }
                });

                this.setState({
                    collectionSchema: schema,
                    currentMapping: {
                        ...this.state.currentMapping,
                        targetCollection: collection,
                        sourceEntity: collection, // Set sourceEntity to match collection name by default
                        fieldMappings: fieldMappings
                    },
                    mappingRules: collectionRules
                });
            }
        } catch (error) {
            toastr.error('Failed to load collection schema');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleMappingRuleSelect = async (ruleId) => {
        if (!ruleId) {
            this.setState({
                selectedMappingRule: null,
                currentMapping: {
                    ...this.state.currentMapping,
                    _id: undefined // Clear _id when deselecting
                }
            });
            return;
        }

        try {
            const response = await getMappingRuleById(ruleId);
            if (response && response.success) {
                const rule = response.data;
                const { collectionSchema } = this.state;

                // Create a map of existing field mappings for quick lookup
                const existingMappingsMap = {};
                rule.fieldMappings.forEach((mapping) => {
                    existingMappingsMap[mapping.targetField] = mapping;
                });

                // Merge with all schema fields to ensure all fields are shown
                const mergedFieldMappings = [];
                const processedFields = new Set();

                Object.keys(collectionSchema).forEach((field) => {
                    const schemaField = collectionSchema[field];

                    // Skip address subfields if we already processed the address object
                    if (field.startsWith('address.') && processedFields.has('address')) {
                        return;
                    }

                    // Handle address object specially
                    if (field === 'address' && schemaField.type === 'Object') {
                        // Check if there are address subfields
                        const addressSubfields = Object.keys(collectionSchema).filter((f) => f.startsWith('address.'));
                        if (addressSubfields.length > 0) {
                            // Mark as processed
                            processedFields.add('address');
                            addressSubfields.forEach((f) => processedFields.add(f));

                            // Check if we have an existing address mapping
                            const existingAddressMapping = existingMappingsMap['address'];

                            mergedFieldMappings.push(
                                existingAddressMapping || {
                                    targetField: 'address',
                                    targetFieldType: 'AddressObject',
                                    required: schemaField.required || false,
                                    sourceField: '',
                                    sourceType: 'addressParse',
                                    sourceConfig: {},
                                    transformation: 'none',
                                    transformationConfig: {},
                                    validation: {},
                                    isAddressField: true
                                }
                            );
                            return;
                        }
                    }

                    // Regular field processing
                    if (!processedFields.has(field)) {
                        processedFields.add(field);

                        // If we have an existing mapping for this field, use it
                        if (existingMappingsMap[field]) {
                            mergedFieldMappings.push(existingMappingsMap[field]);
                        } else {
                            // Otherwise, create a default mapping for this field
                            mergedFieldMappings.push({
                                targetField: field,
                                targetFieldType: schemaField.type || 'String',
                                required: schemaField.required || false,
                                sourceField: '',
                                sourceType: 'direct',
                                sourceConfig: {},
                                transformation: 'none',
                                transformationConfig: {},
                                validation: {}
                            });
                        }
                    }
                });

                this.setState({
                    selectedMappingRule: rule,
                    currentMapping: {
                        ...rule,
                        fieldMappings: mergedFieldMappings
                        // Keep the _id so we know we're editing this rule
                    }
                });
            }
        } catch (error) {
            toastr.error('Failed to load mapping rule');
        }
    };

    handleFieldMappingChange = (index, field, value) => {
        const fieldMappings = [...this.state.currentMapping.fieldMappings];
        fieldMappings[index] = {
            ...fieldMappings[index],
            [field]: value
        };

        // Reset source config when changing source type
        if (field === 'sourceType' && value !== fieldMappings[index].sourceType) {
            fieldMappings[index].sourceConfig = {};

            // Set default values for specific source types
            if (value === 'split') {
                fieldMappings[index].sourceConfig = {
                    splitDelimiter: ';',
                    splitIndex: 0
                };
            } else if (value === 'splitLookup') {
                fieldMappings[index].sourceConfig = {
                    splitDelimiter: ';',
                    splitMode: 'single',
                    splitIndex: 0,
                    lookupCollection: '',
                    lookupTargetField: 'name',
                    lookupReturnField: '_id',
                    returnOriginalIfNotFound: false
                };
            } else if (value === 'splitArray') {
                fieldMappings[index].sourceConfig = {
                    splitDelimiter: ',',
                    trimValues: true,
                    removeEmpty: true
                };
            } else if (value === 'lookup') {
                fieldMappings[index].sourceConfig = {
                    lookupCollection: '',
                    lookupSourceField: fieldMappings[index].sourceField || '',
                    lookupTargetField: 'name',
                    lookupReturnField: '_id'
                };
            }
        }

        this.setState({
            currentMapping: {
                ...this.state.currentMapping,
                fieldMappings
            }
        });
    };

    handleValidationChange = (index, validationType, value) => {
        const fieldMappings = [...this.state.currentMapping.fieldMappings];
        if (!fieldMappings[index].validation) {
            fieldMappings[index].validation = {};
        }

        if (value === false || value === '' || value === null || value === undefined) {
            // Remove validation rule if disabled
            delete fieldMappings[index].validation[validationType];
        } else {
            // Set validation rule
            fieldMappings[index].validation[validationType] = value;
        }

        this.setState({
            currentMapping: {
                ...this.state.currentMapping,
                fieldMappings
            }
        });
    };

    handleSourceConfigChange = (index, configField, value) => {
        const fieldMappings = [...this.state.currentMapping.fieldMappings];
        fieldMappings[index].sourceConfig = {
            ...fieldMappings[index].sourceConfig,
            [configField]: value
        };

        this.setState({
            currentMapping: {
                ...this.state.currentMapping,
                fieldMappings
            }
        });
    };

    handleTransformationConfigChange = (index, configField, value) => {
        const fieldMappings = [...this.state.currentMapping.fieldMappings];
        if (!fieldMappings[index].transformationConfig) {
            fieldMappings[index].transformationConfig = {};
        }
        fieldMappings[index].transformationConfig[configField] = value;

        this.setState({
            currentMapping: {
                ...this.state.currentMapping,
                fieldMappings
            }
        });
    };

    saveMappingRule = async () => {
        const { currentMapping, selectedMappingRule } = this.state;

        if (!currentMapping.name || !currentMapping.targetCollection || !currentMapping.sourceEntity) {
            toastr.error('Please provide a name, select a collection, and specify the source entity');
            return;
        }

        try {
            const mappingData = {
                ...currentMapping,
                fieldMappings: currentMapping.fieldMappings
                    .filter((fm) => fm.sourceField || fm.sourceType !== 'direct')
                    .map((fm) => {
                        // Clean up empty validation objects
                        const cleaned = { ...fm };
                        if (cleaned.validation && Object.keys(cleaned.validation).length === 0) {
                            delete cleaned.validation;
                        }
                        
                        // Clean up empty transformationConfig objects
                        if (cleaned.transformationConfig && Object.keys(cleaned.transformationConfig).length === 0) {
                            delete cleaned.transformationConfig;
                        }
                        
                        // Ensure lookupSourceField is set for lookup types
                        if (cleaned.sourceType === 'lookup' && cleaned.sourceField && cleaned.sourceConfig) {
                            cleaned.sourceConfig.lookupSourceField = cleaned.sourceField;
                        }
                        
                        return cleaned;
                    })
            };

            // Check if we're updating an existing rule (either by having selectedMappingRule or matching _id)
            const isUpdate = selectedMappingRule && (selectedMappingRule._id || currentMapping._id);
            const ruleId = selectedMappingRule?._id || currentMapping._id;

            const response = isUpdate
                ? await updateMappingRule(ruleId, mappingData)
                : await createMappingRule(mappingData);

            if (response && response.success) {
                toastr.success(`Mapping rule ${isUpdate ? 'updated' : 'created'} successfully`);
                this.setState({
                    showMappingDialog: false,
                    editingMapping: null,
                    selectedMappingRule: response.data // Set the newly created/updated rule as selected
                });
                this.loadMappingRules();
            } else {
                toastr.error(response?.message || `Failed to ${isUpdate ? 'update' : 'create'} mapping rule`);
            }
        } catch (error) {
            console.error('Save mapping rule error:', error);
            const isUpdate = selectedMappingRule && (selectedMappingRule._id || currentMapping._id);
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                `Failed to ${isUpdate ? 'update' : 'create'} mapping rule`;

            // Check for duplicate name error
            if (errorMessage.includes('duplicate') || errorMessage.includes('unique')) {
                toastr.error('A mapping rule with this name already exists. Please choose a different name.');
            } else {
                toastr.error(errorMessage);
            }
        }
    };

    previewMigration = async () => {
        const { selectedMappingRule, selectedEntityType } = this.state;

        if (!selectedMappingRule || !selectedEntityType) {
            toastr.error('Please select a mapping rule and entity type');
            return;
        }

        try {
            const response = await previewLegacyMigration(selectedMappingRule._id, selectedEntityType, 5);

            if (response && response.success) {
                // Show preview in a dialog or section
                console.log('Preview:', response.data);
                toastr.info('Preview generated - check console for now');
            }
        } catch (error) {
            toastr.error('Failed to generate preview');
        }
    };

    executeMigration = async () => {
        const { selectedMappingRule, selectedEntityType, entityCount } = this.state;

        if (!selectedMappingRule || !selectedEntityType) {
            toastr.error('Please select a mapping rule and entity type');
            return;
        }

        const confirm = window.confirm(
            `Are you sure you want to start migration for ${entityCount.toLocaleString()} records ` +
                `from "${selectedEntityType}" using mapping rule "${selectedMappingRule.name}"?`
        );

        if (!confirm) return;

        this.setState({ migrationInProgress: true });

        try {
            const response = await executeLegacyMigration(
                selectedMappingRule._id,
                selectedEntityType,
                selectedMappingRule.options
            );

            if (response && response.success) {
                const jobId = response.data.jobId;
                toastr.success('Migration started successfully');

                // Switch to status tab
                this.setState({
                    activeTab: 'status',
                    currentJob: response.data
                });

                // Start polling for status
                this.startStatusPolling(jobId);

                // Reload jobs
                this.loadMigrationJobs();
            } else {
                toastr.error(response?.message || 'Failed to start migration');
                this.setState({ migrationInProgress: false });
            }
        } catch (error) {
            console.error('Execute migration error:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to start migration';
            toastr.error(errorMessage);
            this.setState({ migrationInProgress: false });
        }
    };

    startStatusPolling = (jobId) => {
        this.statusCheckInterval = setInterval(async () => {
            try {
                const response = await getMigrationStatus(jobId);
                if (response && response.success) {
                    const status = response.data;

                    // Update current job status
                    this.setState({ currentJob: status });

                    // Stop polling if job is complete
                    if (['completed', 'failed', 'cancelled'].includes(status.status)) {
                        clearInterval(this.statusCheckInterval);
                        this.setState({ migrationInProgress: false });
                        this.loadMigrationJobs();

                        if (status.status === 'completed') {
                            toastr.success('Migration completed successfully');
                        } else if (status.status === 'failed') {
                            toastr.error('Migration failed');
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to get migration status', error);
            }
        }, 2000); // Poll every 2 seconds
    };

    handlePreviewClick = (event, columnName) => {
        const { entitySample } = this.state;
        if (entitySample.length === 0) return;

        if (!columnName) return;

        // Extract values for this column from entity sample data
        const previewData = entitySample.slice(0, 5).map((record) => {
            // Access the column value from the record's data
            const value = record?.data?.[columnName];
            return value !== undefined ? String(value) : '';
        });

        this.setState({
            previewAnchorEl: event.currentTarget,
            previewColumn: columnName,
            previewData: previewData
        });
    };

    handlePreviewClose = () => {
        this.setState({
            previewAnchorEl: null,
            previewColumn: null,
            previewData: []
        });
    };

    renderSourceTypeConfig = (mapping, index, entityHeaders) => {
        const { sourceType, sourceConfig } = mapping;

        switch (sourceType) {
            case 'direct':
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Entity Field</InputLabel>
                            <Select
                                value={mapping.sourceField || ''}
                                onChange={(e) => this.handleFieldMappingChange(index, 'sourceField', e.target.value)}
                                label="Entity Field"
                            >
                                <MenuItem value="">None</MenuItem>
                                {entityHeaders.map((header, idx) => (
                                    <MenuItem key={idx} value={header}>
                                        <div
                                            style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                                        >
                                            <span>{header}</span>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {mapping.sourceField && (
                            <Tooltip title="Preview data">
                                <IconButton
                                    size="small"
                                    onClick={(e) => this.handlePreviewClick(e, mapping.sourceField)}
                                    style={{ flexShrink: 0 }}
                                >
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                );

            case 'concatenated':
                return (
                    <div className="source-config">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FormControl fullWidth size="small" className="mb-2">
                                <InputLabel>Concatenate Fields</InputLabel>
                                <Select
                                    multiple
                                    value={sourceConfig.concatenateFields || []}
                                    onChange={(e) =>
                                        this.handleSourceConfigChange(index, 'concatenateFields', e.target.value)
                                    }
                                    label="Concatenate Fields"
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {sourceConfig.concatenateFields && sourceConfig.concatenateFields.length > 0 && (
                                <Tooltip title="Preview first field">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => this.handlePreviewClick(e, sourceConfig.concatenateFields[0])}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                        <TextField
                            fullWidth
                            size="small"
                            label="Separator"
                            value={sourceConfig.separator || ' '}
                            onChange={(e) => this.handleSourceConfigChange(index, 'separator', e.target.value)}
                        />
                    </div>
                );

            case 'default':
                // Check if the target field type is boolean
                if (mapping.targetFieldType === 'Boolean') {
                    return (
                        <FormControl fullWidth size="small">
                            <InputLabel>Default Value</InputLabel>
                            <Select
                                value={sourceConfig.defaultValue !== undefined ? String(sourceConfig.defaultValue) : ''}
                                onChange={(e) =>
                                    this.handleSourceConfigChange(index, 'defaultValue', e.target.value === 'true')
                                }
                                label="Default Value"
                            >
                                <MenuItem value="">Select Value</MenuItem>
                                <MenuItem value="true">True</MenuItem>
                                <MenuItem value="false">False</MenuItem>
                            </Select>
                        </FormControl>
                    );
                }

                // For non-boolean types, show regular text input
                return (
                    <TextField
                        fullWidth
                        size="small"
                        label="Default Value"
                        value={sourceConfig.defaultValue || ''}
                        onChange={(e) => this.handleSourceConfigChange(index, 'defaultValue', e.target.value)}
                    />
                );

            case 'computed':
                return (
                    <div className="source-config">
                        <FormControl fullWidth size="small" className="mb-2">
                            <InputLabel>Compute Function</InputLabel>
                            <Select
                                value={sourceConfig.computeFunction || ''}
                                onChange={(e) =>
                                    this.handleSourceConfigChange(index, 'computeFunction', e.target.value)
                                }
                                label="Compute Function"
                            >
                                <MenuItem value="">Select Function</MenuItem>
                                <MenuItem value="generateUsername">Generate Username</MenuItem>
                                <MenuItem value="calculateAge">Calculate Age</MenuItem>
                                <MenuItem value="generatePatientId">Generate Patient ID</MenuItem>
                            </Select>
                        </FormControl>
                        {sourceConfig.computeFunction && (
                            <FormControl fullWidth size="small">
                                <InputLabel>Parameters</InputLabel>
                                <Select
                                    multiple
                                    value={sourceConfig.computeParams || []}
                                    onChange={(e) =>
                                        this.handleSourceConfigChange(index, 'computeParams', e.target.value)
                                    }
                                    label="Parameters"
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </div>
                );

            case 'lookup':
                // Ensure lookupSourceField is set from sourceField
                if (mapping.sourceField && !sourceConfig.lookupSourceField) {
                    sourceConfig.lookupSourceField = mapping.sourceField;
                }
                
                return (
                    <div className="source-config">
                        <TextField
                            fullWidth
                            size="small"
                            label="Lookup Collection"
                            value={sourceConfig.lookupCollection || ''}
                            onChange={(e) => this.handleSourceConfigChange(index, 'lookupCollection', e.target.value)}
                            className="mb-2"
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Source Field</InputLabel>
                                <Select
                                    value={mapping.sourceField || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        // Update both sourceField and lookupSourceField in one state update
                                        const fieldMappings = [...this.state.currentMapping.fieldMappings];
                                        fieldMappings[index] = {
                                            ...fieldMappings[index],
                                            sourceField: value,
                                            sourceConfig: {
                                                ...fieldMappings[index].sourceConfig,
                                                lookupSourceField: value
                                            }
                                        };
                                        this.setState({
                                            currentMapping: {
                                                ...this.state.currentMapping,
                                                fieldMappings
                                            }
                                        });
                                    }}
                                    label="Source Field"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {mapping.sourceField && (
                                <Tooltip title="Preview data">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => this.handlePreviewClick(e, mapping.sourceField)}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                        <TextField
                            fullWidth
                            size="small"
                            label="Target Field"
                            value={sourceConfig.lookupTargetField || ''}
                            onChange={(e) => this.handleSourceConfigChange(index, 'lookupTargetField', e.target.value)}
                            className="mb-2"
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Return Field"
                            value={sourceConfig.lookupReturnField || '_id'}
                            onChange={(e) => this.handleSourceConfigChange(index, 'lookupReturnField', e.target.value)}
                        />
                    </div>
                );

            case 'addressParse':
                return (
                    <div className="source-config">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Address Field</InputLabel>
                                <Select
                                    value={mapping.sourceField || ''}
                                    onChange={(e) =>
                                        this.handleFieldMappingChange(index, 'sourceField', e.target.value)
                                    }
                                    label="Address Field"
                                >
                                    <MenuItem value="">Select Field</MenuItem>
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {mapping.sourceField && (
                                <Tooltip title="Preview data">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => this.handlePreviewClick(e, mapping.sourceField)}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                        <Alert severity="info" className="mt-2">
                            <Typography variant="body2">
                                This will parse a full address string into separate fields: address1, address2, city,
                                state, and zip.
                            </Typography>
                            <Typography variant="caption" display="block" className="mt-1">
                                Supported formats:
                                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                    <li>"123 Main St, Apt 4B, New York, NY 10001"</li>
                                    <li>"123 Main St, New York, NY, 10001"</li>
                                    <li>"123 Main St New York NY 10001"</li>
                                </ul>
                            </Typography>
                        </Alert>
                    </div>
                );

            case 'split':
                return (
                    <div className="source-config">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Source Field</InputLabel>
                                <Select
                                    value={mapping.sourceField || ''}
                                    onChange={(e) =>
                                        this.handleFieldMappingChange(index, 'sourceField', e.target.value)
                                    }
                                    label="Source Field"
                                >
                                    <MenuItem value="">Select Field</MenuItem>
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {mapping.sourceField && (
                                <Tooltip title="Preview data">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => this.handlePreviewClick(e, mapping.sourceField)}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>

                        <TextField
                            fullWidth
                            size="small"
                            label="Split Delimiter"
                            value={sourceConfig.splitDelimiter !== undefined ? sourceConfig.splitDelimiter : ';'}
                            onChange={(e) => this.handleSourceConfigChange(index, 'splitDelimiter', e.target.value)}
                            className="mb-2"
                            helperText="Character that separates values (e.g., ; or ,)"
                            onBlur={(e) => {
                                // Ensure the default value is saved if not changed
                                if (!sourceConfig.splitDelimiter) {
                                    this.handleSourceConfigChange(index, 'splitDelimiter', ';');
                                }
                            }}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Split Index"
                            type="number"
                            value={sourceConfig.splitIndex !== undefined ? sourceConfig.splitIndex : 0}
                            onChange={(e) =>
                                this.handleSourceConfigChange(index, 'splitIndex', parseInt(e.target.value) || 0)
                            }
                            helperText="Which part to extract (0 = first, 1 = second, etc.)"
                        />

                        <Alert severity="info" className="mt-2">
                            <Typography variant="body2">
                                This will split the field value and extract a specific part.
                            </Typography>
                            <Typography variant="caption" display="block" className="mt-1">
                                Example: "Specimen Type;Urine" with delimiter ";" and index 1 → "Urine"
                            </Typography>
                        </Alert>
                    </div>
                );

            case 'splitLookup':
                return (
                    <div className="source-config">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Source Field</InputLabel>
                                <Select
                                    value={mapping.sourceField || ''}
                                    onChange={(e) =>
                                        this.handleFieldMappingChange(index, 'sourceField', e.target.value)
                                    }
                                    label="Source Field"
                                >
                                    <MenuItem value="">Select Field</MenuItem>
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {mapping.sourceField && (
                                <Tooltip title="Preview data">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => this.handlePreviewClick(e, mapping.sourceField)}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>

                        <TextField
                            fullWidth
                            size="small"
                            label="Split Delimiter"
                            value={sourceConfig.splitDelimiter !== undefined ? sourceConfig.splitDelimiter : ';'}
                            onChange={(e) => this.handleSourceConfigChange(index, 'splitDelimiter', e.target.value)}
                            className="mb-2"
                            helperText="Character that separates values (e.g., ; or ,)"
                        />

                        <FormControl fullWidth size="small" className="mb-2">
                            <InputLabel>Split Mode</InputLabel>
                            <Select
                                value={sourceConfig.splitMode || 'single'}
                                onChange={(e) => this.handleSourceConfigChange(index, 'splitMode', e.target.value)}
                                label="Split Mode"
                            >
                                <MenuItem value="single">Single Value (by index)</MenuItem>
                                <MenuItem value="multiple">Multiple Values (array)</MenuItem>
                            </Select>
                        </FormControl>

                        {sourceConfig.splitMode !== 'multiple' && (
                            <TextField
                                fullWidth
                                size="small"
                                label="Split Index"
                                type="number"
                                value={sourceConfig.splitIndex !== undefined ? sourceConfig.splitIndex : 0}
                                onChange={(e) =>
                                    this.handleSourceConfigChange(index, 'splitIndex', parseInt(e.target.value) || 0)
                                }
                                className="mb-2"
                                helperText="Which part to extract (0 = first, 1 = second, etc.)"
                            />
                        )}

                        <FormControl fullWidth size="small" className="mb-2">
                            <InputLabel>Lookup Collection</InputLabel>
                            <Select
                                value={sourceConfig.lookupCollection || ''}
                                onChange={(e) => this.handleSourceConfigChange(index, 'lookupCollection', e.target.value)}
                                label="Lookup Collection"
                            >
                                <MenuItem value="">Select Collection</MenuItem>
                                <MenuItem value="analytes">Analytes</MenuItem>
                                <MenuItem value="tests">Tests</MenuItem>
                                <MenuItem value="physicians">Physicians</MenuItem>
                                <MenuItem value="facilities">Facilities</MenuItem>
                                <MenuItem value="patients">Patients</MenuItem>
                                <MenuItem value="companies">Companies</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            size="small"
                            label="Lookup Field"
                            value={sourceConfig.lookupTargetField || 'name'}
                            onChange={(e) => this.handleSourceConfigChange(index, 'lookupTargetField', e.target.value)}
                            className="mb-2"
                            helperText="Field to match in the lookup collection"
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Return Field"
                            value={sourceConfig.lookupReturnField || '_id'}
                            onChange={(e) => this.handleSourceConfigChange(index, 'lookupReturnField', e.target.value)}
                            className="mb-2"
                            helperText="Field to return from matched documents"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sourceConfig.returnOriginalIfNotFound || false}
                                    onChange={(e) =>
                                        this.handleSourceConfigChange(index, 'returnOriginalIfNotFound', e.target.checked)
                                    }
                                    size="small"
                                />
                            }
                            label="Return original value if lookup fails"
                        />

                        <Alert severity="info" className="mt-2">
                            <Typography variant="body2">
                                This will split the field value, then lookup each part in the specified collection.
                            </Typography>
                            <Typography variant="caption" display="block" className="mt-1">
                                Example: "Facility;ABC Clinic" → Split by ";" → Lookup "ABC Clinic" → Return facility ID
                            </Typography>
                        </Alert>
                    </div>
                );

            case 'multiValueLookup':
                return (
                    <div className="source-config">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Source Field</InputLabel>
                                <Select
                                    value={mapping.sourceField || ''}
                                    onChange={(e) =>
                                        this.handleFieldMappingChange(index, 'sourceField', e.target.value)
                                    }
                                    label="Source Field"
                                >
                                    <MenuItem value="">Select Field</MenuItem>
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {mapping.sourceField && (
                                <Tooltip title="Preview data">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => this.handlePreviewClick(e, mapping.sourceField)}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>

                        <FormControl fullWidth size="small" className="mb-2">
                            <InputLabel>Lookup Collection</InputLabel>
                            <Select
                                value={sourceConfig.lookupCollection || ''}
                                onChange={(e) =>
                                    this.handleSourceConfigChange(index, 'lookupCollection', e.target.value)
                                }
                                label="Lookup Collection"
                            >
                                <MenuItem value="">Select Collection</MenuItem>
                                <MenuItem value="analytes">Analytes</MenuItem>
                                <MenuItem value="tests">Tests</MenuItem>
                                <MenuItem value="physicians">Physicians</MenuItem>
                                <MenuItem value="facilities">Facilities</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            size="small"
                            label="Delimiter"
                            value={sourceConfig.delimiter || ';'}
                            onChange={(e) => this.handleSourceConfigChange(index, 'delimiter', e.target.value)}
                            className="mb-2"
                            helperText="Character that separates values (default: ;)"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sourceConfig.skipFirstValue || false}
                                    onChange={(e) =>
                                        this.handleSourceConfigChange(index, 'skipFirstValue', e.target.checked)
                                    }
                                    size="small"
                                />
                            }
                            label="Skip first value (e.g., skip 'Analyt' in 'Analyt;Pregabalin;Gabapentin')"
                        />

                        {sourceConfig.lookupCollection && (
                            <>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Lookup Field"
                                    value={sourceConfig.lookupTargetField || 'name'}
                                    onChange={(e) => {
                                        // Update both fields at once
                                        const fieldMappings = [...this.state.currentMapping.fieldMappings];
                                        fieldMappings[index].sourceConfig = {
                                            ...fieldMappings[index].sourceConfig,
                                            lookupTargetField: e.target.value,
                                            lookupSourceField: 'name' // Set this for compatibility with dataTransformer
                                        };

                                        this.setState({
                                            currentMapping: {
                                                ...this.state.currentMapping,
                                                fieldMappings
                                            }
                                        });
                                    }}
                                    className="mb-2 mt-2"
                                    helperText="Field to match in the lookup collection"
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Return Field"
                                    value={sourceConfig.lookupReturnField || '_id'}
                                    onChange={(e) =>
                                        this.handleSourceConfigChange(index, 'lookupReturnField', e.target.value)
                                    }
                                    helperText="Field to return from matched documents"
                                />
                            </>
                        )}

                        <Alert severity="info" className="mt-2">
                            <Typography variant="body2">
                                This will parse delimited values and lookup each one to create an array of IDs.
                            </Typography>
                            <Typography variant="caption" display="block" className="mt-1">
                                Example: "Analyt;Pregabalin;Gabapentin" → [pregabalinId, gabapentinId]
                            </Typography>
                        </Alert>
                    </div>
                );

            case 'splitArray':
                return (
                    <div className="source-config">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Source Field</InputLabel>
                                <Select
                                    value={mapping.sourceField || ''}
                                    onChange={(e) =>
                                        this.handleFieldMappingChange(index, 'sourceField', e.target.value)
                                    }
                                    label="Source Field"
                                >
                                    <MenuItem value="">Select Field</MenuItem>
                                    {entityHeaders.map((header, idx) => (
                                        <MenuItem key={idx} value={header}>
                                            {header}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {mapping.sourceField && (
                                <Tooltip title="Preview data">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => this.handlePreviewClick(e, mapping.sourceField)}
                                        style={{ flexShrink: 0 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>

                        <TextField
                            fullWidth
                            size="small"
                            label="Delimiter"
                            value={sourceConfig.splitDelimiter || ','}
                            onChange={(e) => this.handleSourceConfigChange(index, 'splitDelimiter', e.target.value)}
                            className="mb-2"
                            helperText="Character that separates values (default: comma)"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sourceConfig.trimValues !== false}
                                    onChange={(e) =>
                                        this.handleSourceConfigChange(index, 'trimValues', e.target.checked)
                                    }
                                    size="small"
                                />
                            }
                            label="Trim whitespace from values"
                            className="mb-2"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sourceConfig.removeEmpty !== false}
                                    onChange={(e) =>
                                        this.handleSourceConfigChange(index, 'removeEmpty', e.target.checked)
                                    }
                                    size="small"
                                />
                            }
                            label="Remove empty values"
                            className="mb-2"
                        />

                        <Alert severity="info" className="mt-2">
                            <Typography variant="body2">
                                This will split a delimited string into an array of values.
                            </Typography>
                            <Typography variant="caption" display="block" className="mt-1">
                                Example: "A,B,C" → ["A", "B", "C"]
                            </Typography>
                        </Alert>
                    </div>
                );

            default:
                return null;
        }
    };

    renderFieldMappingRow = (mapping, index, entityHeaders) => {
        const isRequired = mapping.required || mapping.validation?.required;
        const isMapped =
            mapping.sourceField ||
            mapping.sourceType !== 'direct' ||
            (mapping.sourceType === 'direct' && mapping.sourceField);

        return (
            <Accordion key={index} className="field-mapping-row">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div className="field-summary">
                        <div className="field-info">
                            <Typography variant="subtitle1" className="field-name">
                                {mapping.targetField}
                                {mapping.isAddressField && ' (Full Address)'}
                                {isRequired && <span className="required-marker">*</span>}
                            </Typography>
                            <Chip
                                label={mapping.isAddressField ? 'Address' : mapping.targetFieldType}
                                size="small"
                                className="field-type-chip"
                            />
                            {isMapped ? (
                                <CheckCircleIcon className="status-icon mapped" />
                            ) : isRequired ? (
                                <WarningIcon className="status-icon unmapped" />
                            ) : (
                                <InfoIcon className="status-icon optional" />
                            )}
                        </div>
                        <div className="mapping-preview">
                            {mapping.sourceType === 'direct' && mapping.sourceField && (
                                <span>← {mapping.sourceField}</span>
                            )}
                            {mapping.sourceType === 'concatenated' && (
                                <span>← Concatenate: {mapping.sourceConfig.concatenateFields?.join(', ')}</span>
                            )}
                            {mapping.sourceType === 'default' && (
                                <span>← Default: {mapping.sourceConfig.defaultValue}</span>
                            )}
                            {mapping.sourceType === 'computed' && (
                                <span>← Compute: {mapping.sourceConfig.computeFunction}</span>
                            )}
                            {mapping.sourceType === 'lookup' && (
                                <span>← Lookup from {mapping.sourceConfig.lookupCollection}</span>
                            )}
                            {mapping.sourceType === 'addressParse' && (
                                <span>← Parse address: {mapping.sourceField}</span>
                            )}
                            {mapping.sourceType === 'split' && <span>← Split: {mapping.sourceField}</span>}
                            {mapping.sourceType === 'splitLookup' && (
                                <span>← Split & Lookup: {mapping.sourceField}</span>
                            )}
                            {mapping.sourceType === 'multiValueLookup' && (
                                <span>← Parse array: {mapping.sourceField}</span>
                            )}
                        </div>
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    <div className="row">
                        <div className="col-md-4">
                            <FormControl fullWidth size="small" className="mb-3">
                                <InputLabel>Source Type</InputLabel>
                                <Select
                                    value={mapping.sourceType || 'direct'}
                                    onChange={(e) => this.handleFieldMappingChange(index, 'sourceType', e.target.value)}
                                    label="Source Type"
                                >
                                    <MenuItem value="direct">Direct Mapping</MenuItem>
                                    <MenuItem value="concatenated">Concatenate Fields</MenuItem>
                                    <MenuItem value="computed">Computed Value</MenuItem>
                                    <MenuItem value="default">Default Value</MenuItem>
                                    <MenuItem value="lookup">Lookup</MenuItem>
                                    <MenuItem value="split">Split Field</MenuItem>
                                    <MenuItem value="splitLookup">Split & Lookup</MenuItem>
                                    <MenuItem value="splitArray">Split Array</MenuItem>
                                    <MenuItem value="addressParse">Parse Full Address</MenuItem>
                                    <MenuItem value="multiValueLookup">Multi-Value Lookup (Array)</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div className="col-md-4">{this.renderSourceTypeConfig(mapping, index, entityHeaders)}</div>
                        <div className="col-md-4">
                            <FormControl fullWidth size="small">
                                <InputLabel>Transformation</InputLabel>
                                <Select
                                    value={mapping.transformation || 'none'}
                                    onChange={(e) =>
                                        this.handleFieldMappingChange(index, 'transformation', e.target.value)
                                    }
                                    label="Transformation"
                                >
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="lowercase">Lowercase</MenuItem>
                                    <MenuItem value="uppercase">Uppercase</MenuItem>
                                    <MenuItem value="trim">Trim Spaces</MenuItem>
                                    <MenuItem value="date">Parse Date</MenuItem>
                                    <MenuItem value="number">Parse Number</MenuItem>
                                    <MenuItem value="boolean">Parse Boolean</MenuItem>
                                    <MenuItem value="phone">Format Phone</MenuItem>
                                    <MenuItem value="ssn">Format SSN</MenuItem>
                                    <MenuItem value="email">Format Email</MenuItem>
                                    <MenuItem value="statusMap">Status Mapping</MenuItem>
                                </Select>
                            </FormControl>
                            
                            {/* Date transformation config */}
                            {mapping.transformation === 'date' && (
                                <div className="mt-3">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Input Date Format"
                                        value={mapping.transformationConfig?.dateFormat || 'MM/DD/YYYY'}
                                        onChange={(e) => this.handleTransformationConfigChange(index, 'dateFormat', e.target.value)}
                                        helperText="Format of dates in source data (e.g., MM/DD/YYYY, YYYY-MM-DD)"
                                        className="mb-2"
                                    />
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Output Format</InputLabel>
                                        <Select
                                            value={mapping.transformationConfig?.outputFormat || ''}
                                            onChange={(e) => this.handleTransformationConfigChange(index, 'outputFormat', e.target.value)}
                                            label="Output Format"
                                        >
                                            <MenuItem value="">Date Object (default)</MenuItem>
                                            <MenuItem value="YYYYMMDDHHmmss">YYYYMMDDHHmmss</MenuItem>
                                            <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                                            <MenuItem value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss</MenuItem>
                                            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                                            <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Validation Settings Row */}
                    <div className="row mt-3">
                        <div className="col-12">
                            <Typography variant="subtitle2" className="mb-2">
                                Validation Rules
                            </Typography>
                            <div className="validation-controls">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mapping.validation?.required || false}
                                            onChange={(e) =>
                                                this.handleValidationChange(index, 'required', e.target.checked)
                                            }
                                            size="small"
                                        />
                                    }
                                    label="Required"
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mapping.validation?.unique || false}
                                            onChange={(e) =>
                                                this.handleValidationChange(index, 'unique', e.target.checked)
                                            }
                                            size="small"
                                        />
                                    }
                                    label="Unique"
                                />

                                {/* Show enum values if they exist in schema */}
                                {this.state.collectionSchema[mapping.targetField]?.enum &&
                                    Array.isArray(this.state.collectionSchema[mapping.targetField].enum) && (
                                        <div className="mt-2">
                                            <Typography variant="caption" display="block">
                                                Allowed values:{' '}
                                                {this.state.collectionSchema[mapping.targetField].enum.join(', ')}
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={mapping.validation?.enum !== undefined}
                                                        onChange={(e) =>
                                                            this.handleValidationChange(
                                                                index,
                                                                'enum',
                                                                e.target.checked
                                                                    ? this.state.collectionSchema[mapping.targetField]
                                                                          .enum
                                                                    : false
                                                            )
                                                        }
                                                        size="small"
                                                    />
                                                }
                                                label="Enforce enum values"
                                            />
                                        </div>
                                    )}

                                {/* Length validation for string fields */}
                                {mapping.targetFieldType === 'String' && (
                                    <div className="mt-2">
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Min Length"
                                            value={mapping.validation?.minLength || ''}
                                            onChange={(e) =>
                                                this.handleValidationChange(
                                                    index,
                                                    'minLength',
                                                    e.target.value ? parseInt(e.target.value) : ''
                                                )
                                            }
                                            style={{ width: 120, marginRight: 10 }}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Max Length"
                                            value={mapping.validation?.maxLength || ''}
                                            onChange={(e) =>
                                                this.handleValidationChange(
                                                    index,
                                                    'maxLength',
                                                    e.target.value ? parseInt(e.target.value) : ''
                                                )
                                            }
                                            style={{ width: 120 }}
                                        />
                                    </div>
                                )}

                                {/* Range validation for number fields */}
                                {mapping.targetFieldType === 'Number' && (
                                    <div className="mt-2">
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Min Value"
                                            value={mapping.validation?.min || ''}
                                            onChange={(e) =>
                                                this.handleValidationChange(
                                                    index,
                                                    'min',
                                                    e.target.value ? parseFloat(e.target.value) : ''
                                                )
                                            }
                                            style={{ width: 120, marginRight: 10 }}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Max Value"
                                            value={mapping.validation?.max || ''}
                                            onChange={(e) =>
                                                this.handleValidationChange(
                                                    index,
                                                    'max',
                                                    e.target.value ? parseFloat(e.target.value) : ''
                                                )
                                            }
                                            style={{ width: 120 }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </AccordionDetails>
            </Accordion>
        );
    };

    render() {
        const {
            collections,
            selectedCollection,
            legacyEntityTypes,
            selectedEntityType,
            entityHeaders,
            entitySample,
            entityCount,
            mappingRules,
            selectedMappingRule,
            currentMapping,
            migrationJobs,
            activeTab,
            loading,
            migrationInProgress,
            currentJob,
            previewAnchorEl,
            previewColumn,
            previewData
        } = this.state;

        return (
            <div className="clinic-contain">
                <Breadcrumb />
                <div className="px-5 py-3">
                    <div className="migration-tool-container">
                        <div className="page-header mb-4">
                            <h2 className="page-title">Data Migration Tool</h2>
                            <p className="page-subtitle">
                                Import legacy data from scanned CSV files into the LIMS database
                            </p>
                        </div>
                        <Tab.Container activeKey={activeTab} onSelect={(k) => this.setState({ activeTab: k })}>
                            <Nav variant="tabs" className="mb-4 migration-tabs">
                                <Nav.Item>
                                    <Nav.Link eventKey="upload" className="migration-tab-link">
                                        <span className="tab-number">1</span>
                                        <span className="tab-text">Select Entity</span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="mapping" className="migration-tab-link">
                                        <span className="tab-number">2</span>
                                        <span className="tab-text">Configure Mapping</span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="execute" className="migration-tab-link">
                                        <span className="tab-number">3</span>
                                        <span className="tab-text">Execute Migration</span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="status" className="migration-tab-link">
                                        <span className="tab-number">4</span>
                                        <span className="tab-text">Status & History</span>
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>

                            <Tab.Content>
                                {/* Tab 1: Select Entity Type */}
                                <Tab.Pane eventKey="upload">
                                    <Card className="mb-4">
                                        <Card.Header>
                                            <h4>Select Legacy Entity Type</h4>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="form-group">
                                                <FormControl fullWidth>
                                                    <InputLabel>Entity Type</InputLabel>
                                                    <Select
                                                        value={selectedEntityType}
                                                        onChange={(e) => this.handleEntityTypeChange(e.target.value)}
                                                        label="Entity Type"
                                                        disabled={loading}
                                                    >
                                                        <MenuItem value="">Select Entity Type</MenuItem>
                                                        {legacyEntityTypes.map((entity) => (
                                                            <MenuItem key={entity.entityType} value={entity.entityType}>
                                                                {entity.displayName} (
                                                                {entity.recordCount.toLocaleString()} records)
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <small className="text-muted d-block mt-2">
                                                    Select from the available entity types that have been scanned from
                                                    CSV files.
                                                </small>
                                            </div>

                                            {selectedEntityType && entityCount > 0 && (
                                                <div className="entity-info mt-4">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h5>Entity Information</h5>
                                                        <Badge variant="primary">
                                                            {entityCount.toLocaleString()} records
                                                        </Badge>
                                                    </div>

                                                    {loading ? (
                                                        <div className="text-center p-3">
                                                            <CircularProgress />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <Alert severity="info">
                                                                <strong>Selected:</strong> {selectedEntityType}
                                                                <br />
                                                                <strong>Total Records:</strong>{' '}
                                                                {entityCount.toLocaleString()}
                                                                <br />
                                                                <strong>Available Fields:</strong>{' '}
                                                                {entityHeaders.length}
                                                            </Alert>

                                                            <div className="entity-headers mt-3">
                                                                <h6>Available Fields:</h6>
                                                                <div className="headers-container">
                                                                    {entityHeaders.map((header, index) => (
                                                                        <Chip
                                                                            key={index}
                                                                            label={header}
                                                                            size="small"
                                                                            className="header-chip"
                                                                            color="primary"
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {entitySample.length > 0 && (
                                                                <div className="entity-sample mt-4">
                                                                    <h6>Sample Data:</h6>
                                                                    <Table striped bordered hover size="sm">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Field</th>
                                                                                <th>Sample Value</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {entityHeaders
                                                                                .slice(0, 10)
                                                                                .map((header, index) => (
                                                                                    <tr key={index}>
                                                                                        <td>
                                                                                            <strong>{header}</strong>
                                                                                        </td>
                                                                                        <td>
                                                                                            {entitySample[0]?.data?.[
                                                                                                header
                                                                                            ] || (
                                                                                                <span className="text-muted">
                                                                                                    (empty)
                                                                                                </span>
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                        </tbody>
                                                                    </Table>
                                                                    {entityHeaders.length > 10 && (
                                                                        <small className="text-muted">
                                                                            Showing first 10 fields. Total:{' '}
                                                                            {entityHeaders.length} fields.
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Tab.Pane>

                                {/* Tab 2: Configure Mapping */}
                                <Tab.Pane eventKey="mapping">
                                    <Card className="mb-4">
                                        <Card.Header>
                                            <h4>Configure Field Mapping</h4>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="row mb-4">
                                                <div className="col-md-3">
                                                    <FormControl fullWidth>
                                                        <InputLabel>Target Collection</InputLabel>
                                                        <Select
                                                            value={selectedCollection}
                                                            onChange={this.handleCollectionChange}
                                                            label="Target Collection"
                                                            disabled={loading}
                                                        >
                                                            <MenuItem value="">Select Collection</MenuItem>
                                                            {collections.map((col) => (
                                                                <MenuItem key={col.name} value={col.name}>
                                                                    {col.displayName} (Phase {col.phase})
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </div>
                                                <div className="col-md-3">
                                                    <FormControl fullWidth>
                                                        <InputLabel>Use Existing Rule</InputLabel>
                                                        <Select
                                                            value={selectedMappingRule?._id || ''}
                                                            onChange={(e) =>
                                                                this.handleMappingRuleSelect(e.target.value)
                                                            }
                                                            label="Use Existing Rule"
                                                        >
                                                            <MenuItem value="">Create New Mapping</MenuItem>
                                                            {mappingRules.map((rule) => (
                                                                <MenuItem key={rule._id} value={rule._id}>
                                                                    {rule.name}
                                                                    {rule.isSystemDefault && (
                                                                        <Chip
                                                                            label="Default"
                                                                            size="small"
                                                                            className="ml-2"
                                                                        />
                                                                    )}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </div>
                                                <div className="col-md-3">
                                                    <TextField
                                                        fullWidth
                                                        label="Mapping Rule Name"
                                                        value={currentMapping.name}
                                                        onChange={(e) =>
                                                            this.setState({
                                                                currentMapping: {
                                                                    ...currentMapping,
                                                                    name: e.target.value
                                                                }
                                                            })
                                                        }
                                                        placeholder="e.g., Patient Import v1"
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <TextField
                                                        fullWidth
                                                        label="Source Entity Type"
                                                        value={currentMapping.sourceEntity}
                                                        onChange={(e) =>
                                                            this.setState({
                                                                currentMapping: {
                                                                    ...currentMapping,
                                                                    sourceEntity: e.target.value
                                                                }
                                                            })
                                                        }
                                                        placeholder="e.g., Patient"
                                                        helperText="Legacy data entity type (should match selected entity)"
                                                    />
                                                </div>
                                            </div>

                                            {selectedCollection && currentMapping.fieldMappings.length > 0 && (
                                                <div className="field-mappings">
                                                    <div className="mappings-header mb-3">
                                                        <h5>Database Fields → Entity Mapping</h5>
                                                        <div className="mapping-stats">
                                                            <Badge variant="success">
                                                                Mapped:{' '}
                                                                {
                                                                    currentMapping.fieldMappings.filter(
                                                                        (fm) =>
                                                                            fm.sourceField || fm.sourceType !== 'direct'
                                                                    ).length
                                                                }
                                                            </Badge>
                                                            <Badge variant="warning" className="ml-2">
                                                                Required:{' '}
                                                                {
                                                                    currentMapping.fieldMappings.filter(
                                                                        (fm) =>
                                                                            fm.required &&
                                                                            !fm.sourceField &&
                                                                            fm.sourceType === 'direct'
                                                                    ).length
                                                                }
                                                            </Badge>
                                                            <Badge variant="info" className="ml-2">
                                                                Total: {currentMapping.fieldMappings.length}
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    {loading ? (
                                                        <div className="text-center p-5">
                                                            <CircularProgress />
                                                        </div>
                                                    ) : (
                                                        <div className="field-mappings-list">
                                                            {currentMapping.fieldMappings.map((mapping, index) =>
                                                                this.renderFieldMappingRow(
                                                                    mapping,
                                                                    index,
                                                                    entityHeaders
                                                                )
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Duplicate Handling Configuration */}
                                                    <Card className="mt-4 duplicate-config-card">
                                                        <Card.Header>
                                                            <h6 className="mb-0">Duplicate Handling Configuration</h6>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            <div className="row">
                                                                <div className="col-md-6">
                                                                    <FormControl fullWidth size="small">
                                                                        <InputLabel>
                                                                            Unique Fields (for duplicate detection)
                                                                        </InputLabel>
                                                                        <Select
                                                                            multiple
                                                                            value={
                                                                                currentMapping.collectionConfig
                                                                                    ?.uniqueFields || []
                                                                            }
                                                                            onChange={(e) =>
                                                                                this.setState({
                                                                                    currentMapping: {
                                                                                        ...currentMapping,
                                                                                        collectionConfig: {
                                                                                            ...currentMapping.collectionConfig,
                                                                                            uniqueFields: e.target.value
                                                                                        }
                                                                                    }
                                                                                })
                                                                            }
                                                                            label="Unique Fields (for duplicate detection)"
                                                                            renderValue={(selected) =>
                                                                                selected.join(', ')
                                                                            }
                                                                        >
                                                                            {currentMapping.fieldMappings
                                                                                .filter(
                                                                                    (fm) =>
                                                                                        fm.sourceField ||
                                                                                        fm.sourceType !== 'direct'
                                                                                )
                                                                                .map((fm) => (
                                                                                    <MenuItem
                                                                                        key={fm.targetField}
                                                                                        value={fm.targetField}
                                                                                    >
                                                                                        {fm.targetField}
                                                                                    </MenuItem>
                                                                                ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        Select fields that uniquely identify records. If
                                                                        a record with matching values exists, it will be
                                                                        handled according to the update strategy.
                                                                    </Typography>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <FormControl fullWidth size="small">
                                                                        <InputLabel>Update Strategy</InputLabel>
                                                                        <Select
                                                                            value={
                                                                                currentMapping.collectionConfig
                                                                                    ?.updateStrategy || 'skip'
                                                                            }
                                                                            onChange={(e) =>
                                                                                this.setState({
                                                                                    currentMapping: {
                                                                                        ...currentMapping,
                                                                                        collectionConfig: {
                                                                                            ...currentMapping.collectionConfig,
                                                                                            updateStrategy:
                                                                                                e.target.value
                                                                                        }
                                                                                    }
                                                                                })
                                                                            }
                                                                            label="Update Strategy"
                                                                        >
                                                                            <MenuItem value="skip">
                                                                                <div>
                                                                                    <Typography variant="body2">
                                                                                        Skip
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                    >
                                                                                        Skip duplicate records (keep
                                                                                        existing)
                                                                                    </Typography>
                                                                                </div>
                                                                            </MenuItem>
                                                                            <MenuItem value="update">
                                                                                <div>
                                                                                    <Typography variant="body2">
                                                                                        Update
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                    >
                                                                                        Replace all fields with new data
                                                                                    </Typography>
                                                                                </div>
                                                                            </MenuItem>
                                                                            <MenuItem value="merge">
                                                                                <div>
                                                                                    <Typography variant="body2">
                                                                                        Merge
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                    >
                                                                                        Update only provided fields,
                                                                                        keep existing data for empty
                                                                                        fields
                                                                                    </Typography>
                                                                                </div>
                                                                            </MenuItem>
                                                                        </Select>
                                                                    </FormControl>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>

                                                    <div className="mt-4">
                                                        <Button
                                                            variant="primary"
                                                            onClick={this.saveMappingRule}
                                                            disabled={!currentMapping.name || !selectedCollection}
                                                        >
                                                            Save Mapping Rule
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Tab.Pane>

                                {/* Tab 3: Execute Migration */}
                                <Tab.Pane eventKey="execute">
                                    <Card className="mb-4">
                                        <Card.Header>
                                            <h4>Execute Migration</h4>
                                        </Card.Header>
                                        <Card.Body>
                                            {selectedMappingRule && selectedEntityType ? (
                                                <div>
                                                    <Alert variant="info">
                                                        <h5>Migration Summary</h5>
                                                        <ul>
                                                            <li>
                                                                <strong>Mapping Rule:</strong>{' '}
                                                                {selectedMappingRule.name}
                                                            </li>
                                                            <li>
                                                                <strong>Target Collection:</strong>{' '}
                                                                {selectedMappingRule.targetCollection}
                                                            </li>
                                                            <li>
                                                                <strong>Source Entity:</strong> {selectedEntityType}
                                                            </li>
                                                            <li>
                                                                <strong>Total Records:</strong>{' '}
                                                                {entityCount.toLocaleString()}
                                                            </li>
                                                            <li>
                                                                <strong>Batch Size:</strong>{' '}
                                                                {selectedMappingRule.options?.batchSize || 1000}
                                                            </li>
                                                            <li>
                                                                <strong>Duplicate Handling:</strong>
                                                                {selectedMappingRule.collectionConfig
                                                                    ?.updateStrategy === 'skip' && ' Skip duplicates'}
                                                                {selectedMappingRule.collectionConfig
                                                                    ?.updateStrategy === 'update' &&
                                                                    ' Update existing records'}
                                                                {selectedMappingRule.collectionConfig
                                                                    ?.updateStrategy === 'merge' &&
                                                                    ' Merge with existing records'}
                                                                {!selectedMappingRule.collectionConfig
                                                                    ?.updateStrategy && ' Skip duplicates (default)'}
                                                            </li>
                                                            {selectedMappingRule.collectionConfig?.uniqueFields
                                                                ?.length > 0 && (
                                                                <li>
                                                                    <strong>Unique Fields:</strong>{' '}
                                                                    {selectedMappingRule.collectionConfig.uniqueFields.join(
                                                                        ', '
                                                                    )}
                                                                </li>
                                                            )}
                                                        </ul>
                                                    </Alert>

                                                    <div className="mt-4">
                                                        <Button
                                                            variant="info"
                                                            onClick={this.previewMigration}
                                                            className="mr-2"
                                                        >
                                                            Preview Migration
                                                        </Button>
                                                        <Button
                                                            variant="success"
                                                            onClick={this.executeMigration}
                                                            disabled={migrationInProgress}
                                                        >
                                                            {migrationInProgress
                                                                ? 'Migration in Progress...'
                                                                : 'Start Migration'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Alert variant="warning">
                                                    Please complete the following steps:
                                                    <ol>
                                                        <li>
                                                            Select a legacy entity type{' '}
                                                            {!selectedEntityType && (
                                                                <span className="text-danger">
                                                                    (No entity selected!)
                                                                </span>
                                                            )}
                                                        </li>
                                                        <li>Select a collection and configure mapping</li>
                                                        <li>Save or select a mapping rule</li>
                                                    </ol>
                                                </Alert>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Tab.Pane>

                                {/* Tab 4: Status & History */}
                                <Tab.Pane eventKey="status">
                                    <Card className="mb-4">
                                        <Card.Header>
                                            <h4>Migration Status & History</h4>
                                        </Card.Header>
                                        <Card.Body>
                                            {currentJob && migrationInProgress && (
                                                <Alert variant="info" className="mb-4">
                                                    <h5>Current Migration Progress</h5>
                                                    <ProgressBar
                                                        now={currentJob.progress?.percentage || 0}
                                                        label={`${currentJob.progress?.percentage || 0}%`}
                                                        animated
                                                        striped
                                                    />
                                                    <div className="mt-2">
                                                        <small>
                                                            Processed: {currentJob.progress?.processedRows || 0} /{' '}
                                                            {currentJob.progress?.totalRows || 0} rows
                                                        </small>
                                                    </div>
                                                </Alert>
                                            )}

                                            <Table striped bordered hover>
                                                <thead>
                                                    <tr>
                                                        <th>Job ID</th>
                                                        <th>Collection</th>
                                                        <th>Status</th>
                                                        <th>Progress</th>
                                                        <th>Results</th>
                                                        <th>Started</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {migrationJobs.map((job) => (
                                                        <tr key={job._id}>
                                                            <td>{job.jobId}</td>
                                                            <td>{job.targetCollection}</td>
                                                            <td>
                                                                <Badge
                                                                    variant={
                                                                        job.status === 'completed'
                                                                            ? 'success'
                                                                            : job.status === 'failed'
                                                                              ? 'danger'
                                                                              : job.status === 'processing'
                                                                                ? 'warning'
                                                                                : 'secondary'
                                                                    }
                                                                >
                                                                    {job.status}
                                                                </Badge>
                                                            </td>
                                                            <td>{job.progress?.percentage || 0}%</td>
                                                            <td>
                                                                {job.results && (
                                                                    <small>
                                                                        <Tooltip title="Inserted">
                                                                            <span>✓ {job.results.inserted || 0}</span>
                                                                        </Tooltip>
                                                                        {' | '}
                                                                        <Tooltip title="Updated">
                                                                            <span>↻ {job.results.updated || 0}</span>
                                                                        </Tooltip>
                                                                        {' | '}
                                                                        <Tooltip title="Skipped">
                                                                            <span>⊘ {job.results.skipped || 0}</span>
                                                                        </Tooltip>
                                                                        {' | '}
                                                                        <Tooltip title="Failed">
                                                                            <span>⨉ {job.results.failed || 0}</span>
                                                                        </Tooltip>
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td>{new Date(job.createdAt).toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </div>
                </div>

                <style jsx>{`
                    .migration-tool-container {
                        max-width: 1600px;
                        margin: 0 auto;
                    }

                    /* Tab Styles */
                    .migration-tabs {
                        border-bottom: 2px solid #dee2e6;
                        margin-bottom: 2rem !important;
                    }

                    .migration-tabs .nav-item {
                        margin-bottom: -2px;
                    }

                    .migration-tabs .nav-link {
                        border: 1px solid transparent;
                        border-top-left-radius: 0.5rem;
                        border-top-right-radius: 0.5rem;
                        padding: 0.75rem 1.5rem;
                        color: #6c757d;
                        background-color: #f8f9fa;
                        margin-right: 0.5rem;
                        transition: all 0.3s ease;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        min-height: 50px;
                    }

                    .migration-tabs .nav-link:hover {
                        border-color: #e9ecef #e9ecef #dee2e6;
                        background-color: #e9ecef;
                        color: #495057;
                    }

                    .migration-tabs .nav-link.active {
                        color: #495057;
                        background-color: #fff;
                        border-color: #dee2e6 #dee2e6 #fff;
                        border-bottom: 2px solid #fff;
                        font-weight: 600;
                    }

                    .migration-tab-link .tab-number {
                        background-color: #007bff;
                        color: white;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 0.875rem;
                        font-weight: bold;
                    }

                    .migration-tabs .nav-link.active .tab-number {
                        background-color: #28a745;
                    }

                    .migration-tab-link .tab-text {
                        font-size: 0.95rem;
                    }

                    /* Tab Content */
                    .tab-content {
                        background-color: #ffffff;
                        padding: 1rem;
                        border-radius: 0 0 0.5rem 0.5rem;
                    }

                    .tab-pane {
                        animation: fadeIn 0.3s ease-in;
                    }

                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translateY(10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    .headers-container {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;
                        margin-top: 10px;
                    }
                    .header-chip {
                        margin: 2px;
                    }
                    .field-mapping-row {
                        margin-bottom: 8px;
                        border: 1px solid #e0e0e0;
                    }
                    .field-summary {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        width: 100%;
                    }
                    .field-info {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }
                    .field-name {
                        font-weight: 600;
                        margin: 0;
                    }
                    .required-marker {
                        color: #dc3545;
                        margin-left: 4px;
                    }
                    .field-type-chip {
                        background-color: #e3f2fd;
                        color: #1976d2;
                    }
                    .status-icon {
                        font-size: 20px;
                    }
                    .status-icon.mapped {
                        color: #4caf50;
                    }
                    .status-icon.unmapped {
                        color: #ff9800;
                    }
                    .status-icon.optional {
                        color: #9e9e9e;
                    }
                    .mapping-preview {
                        color: #666;
                        font-size: 14px;
                    }
                    .mappings-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .source-config .MuiFormControl-root {
                        margin-bottom: 8px;
                    }
                    .csv-preview-popover {
                        padding: 16px;
                        max-width: 400px;
                    }
                    .csv-preview-popover h6 {
                        margin-bottom: 12px;
                        font-weight: 600;
                    }
                    .csv-preview-popover .preview-row {
                        padding: 8px 12px;
                        margin: 4px 0;
                        background-color: #f5f5f5;
                        border-radius: 4px;
                        font-family: monospace;
                        font-size: 14px;
                        word-break: break-all;
                    }
                    .csv-preview-popover .preview-row:nth-child(even) {
                        background-color: #eeeeee;
                    }
                    .csv-preview-popover .empty-value {
                        color: #999;
                        font-style: italic;
                    }
                    .validation-controls {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 16px;
                        align-items: flex-start;
                    }
                    .validation-controls .MuiFormControlLabel-root {
                        margin-right: 16px;
                    }

                    /* Card improvements */
                    .migration-tool-container .card {
                        box-shadow:
                            0 1px 3px rgba(0, 0, 0, 0.12),
                            0 1px 2px rgba(0, 0, 0, 0.06);
                        border: none;
                        transition: box-shadow 0.3s ease;
                    }

                    .migration-tool-container .card:hover {
                        box-shadow:
                            0 4px 6px rgba(0, 0, 0, 0.1),
                            0 2px 4px rgba(0, 0, 0, 0.06);
                    }

                    .migration-tool-container .card-header {
                        background-color: #f8f9fa;
                        border-bottom: 1px solid #dee2e6;
                        padding: 1rem 1.5rem;
                    }

                    .migration-tool-container .card-header h4,
                    .migration-tool-container .card-header h5,
                    .migration-tool-container .card-header h6 {
                        margin: 0;
                        color: #495057;
                    }

                    /* Page Header */
                    .page-header {
                        text-align: center;
                        padding: 1rem 0;
                        border-bottom: 1px solid #e9ecef;
                        margin-bottom: 2rem;
                    }

                    .page-title {
                        color: #2c3e50;
                        font-size: 2rem;
                        font-weight: 600;
                        margin-bottom: 0.5rem;
                    }

                    .page-subtitle {
                        color: #6c757d;
                        font-size: 1.1rem;
                        margin: 0;
                    }

                    /* Table improvements */
                    .migration-tool-container table {
                        font-size: 0.9rem;
                    }

                    .migration-tool-container .badge {
                        padding: 0.35em 0.65em;
                        font-size: 0.8rem;
                    }

                    /* Button spacing */
                    .migration-tool-container .btn + .btn {
                        margin-left: 0.5rem;
                    }

                    /* Duplicate config card */
                    .duplicate-config-card {
                        background-color: #f8f9fa;
                    }

                    .duplicate-config-card .card-body {
                        background-color: white;
                    }

                    /* Progress bar improvements */
                    .progress {
                        height: 1.5rem;
                        background-color: #e9ecef;
                    }

                    .progress-bar {
                        font-size: 0.875rem;
                        font-weight: 600;
                    }

                    /* Alert improvements */
                    .migration-tool-container .alert {
                        border-radius: 0.5rem;
                        border: none;
                    }

                    .migration-tool-container .alert-info {
                        background-color: #d1ecf1;
                        color: #0c5460;
                    }

                    .migration-tool-container .alert-warning {
                        background-color: #fff3cd;
                        color: #856404;
                    }

                    /* Form control improvements */
                    .migration-tool-container .form-control,
                    .migration-tool-container .MuiInputBase-root {
                        font-size: 0.95rem;
                    }

                    /* Accordion improvements */
                    .field-mapping-row.MuiAccordion-root {
                        margin-bottom: 0.75rem;
                        border: 1px solid #dee2e6;
                        box-shadow: none;
                        border-radius: 0.375rem;
                        overflow: hidden;
                    }

                    .field-mapping-row.MuiAccordion-root:before {
                        display: none;
                    }

                    .field-mapping-row .MuiAccordionSummary-root {
                        background-color: #f8f9fa;
                        min-height: 60px;
                    }

                    .field-mapping-row .MuiAccordionSummary-root:hover {
                        background-color: #e9ecef;
                    }

                    .field-mapping-row.Mui-expanded .MuiAccordionSummary-root {
                        background-color: #e3f2fd;
                    }

                    /* Chip improvements */
                    .MuiChip-root {
                        font-size: 0.8rem;
                        height: 26px;
                    }

                    /* Typography improvements */
                    .MuiTypography-caption {
                        font-size: 0.8rem;
                        line-height: 1.4;
                    }

                    /* Responsive adjustments */
                    @media (max-width: 768px) {
                        .migration-tabs .nav-link {
                            padding: 0.5rem 0.75rem;
                            font-size: 0.875rem;
                            flex-direction: column;
                            gap: 0.25rem;
                        }

                        .migration-tab-link .tab-number {
                            width: 20px;
                            height: 20px;
                            font-size: 0.75rem;
                        }

                        .migration-tab-link .tab-text {
                            font-size: 0.75rem;
                        }

                        .page-title {
                            font-size: 1.5rem;
                        }

                        .page-subtitle {
                            font-size: 1rem;
                        }
                    }
                `}</style>

                {/* CSV Column Preview Popover */}
                <Popover
                    open={Boolean(previewAnchorEl)}
                    anchorEl={previewAnchorEl}
                    onClose={this.handlePreviewClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                >
                    <Box className="csv-preview-popover">
                        <Typography variant="h6">Sample data for: {previewColumn}</Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            First {previewData.length} rows
                        </Typography>
                        {previewData.map((value, index) => (
                            <div key={index} className="preview-row">
                                Row {index + 1}: {value || <span className="empty-value">(empty)</span>}
                            </div>
                        ))}
                        {previewData.length === 0 && (
                            <Typography variant="body2" color="textSecondary">
                                No preview data available
                            </Typography>
                        )}
                    </Box>
                </Popover>
            </div>
        );
    }
}

export default ClinicMigrationTool;
